import { Box, createTheme, ThemeProvider } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import React, { useState, useEffect, forwardRef } from "react";
import Icon from "./Icon";

interface TableDataProps<T> {
  rows: T[];
  columns: GridColDef[];
  pageSize: number[];
  hiddenColumns: string[];
  tableName: string;
  dynamicRowHeight?: boolean;
  processingRowIds?: number;
  refreshFunction?: () => void;
  enhancedStyling?: boolean;
  checkboxSelection?: boolean;
  onRowSelectionChange?: (ids: number[]) => void;
}

const TableData = forwardRef<any, TableDataProps<any>>(
  (
    {
      rows: initialRows,
      columns,
      pageSize,
      hiddenColumns,
      tableName,
      dynamicRowHeight,
      processingRowIds,
      refreshFunction,
      enhancedStyling = false,
      checkboxSelection = false,
      onRowSelectionChange,
    },
    ref
  ) => {
    const PAGINATION_KEY = `pagination_${tableName}`;
    const DENSITY_KEY = `density_${tableName}`;
    const COLUMN_VIS_KEY = `colvis_${tableName}`;
    const [rows, setRows] = useState(initialRows);

    // Update internal rows when props change
    useEffect(() => {
      setRows(initialRows);
    }, [initialRows]);

    const [paginationModel, setPaginationModel] = useState(() => {
      const savedPagination = localStorage.getItem(PAGINATION_KEY);
      return savedPagination ? JSON.parse(savedPagination) : { page: 0, pageSize: pageSize[0] };
    });

    const [density, setDensity] = useState<"compact" | "standard" | "comfortable">(() => {
      try {
        const savedDensity = localStorage.getItem(DENSITY_KEY);
        const parsed = savedDensity ? JSON.parse(savedDensity) : "standard";
        if (["compact", "standard", "comfortable"].includes(parsed)) {
          return parsed;
        }
        return "standard";
      } catch {
        return "standard";
      }
    });

    // Initialize column visibility model from localStorage or default
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
      const saved = localStorage.getItem(COLUMN_VIS_KEY);
      if (saved) {
        return JSON.parse(saved);
      } else {
        return Object.fromEntries(hiddenColumns.map((field) => [field, false]));
      }
    });

    const handleColumnVisibilityModelChange = (newModel: Record<string, boolean>) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem(COLUMN_VIS_KEY, JSON.stringify(newModel));
    };

    const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
      onRowSelectionChange?.((model as (number | string)[]).map((id) => Number(id)));
    };

    useEffect(() => {
      localStorage.setItem(PAGINATION_KEY, JSON.stringify(paginationModel));
    }, [paginationModel, PAGINATION_KEY]);

    useEffect(() => {
      localStorage.setItem(DENSITY_KEY, JSON.stringify(density));
    }, [density, DENSITY_KEY]);

    const getRowClassName = (params: any) => {
      return processingRowIds === params.id ? "processing-row" : "";
    };

    const theme = createTheme({
      typography: {
        fontFamily: "notoSans",
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: "",
        },
      },
    });

    function CustomToolbar() {
      return (
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector slotProps={{ tooltip: { title: "Change density" } }} />
          <Box sx={{ flexGrow: 1 }} />
          <GridToolbarQuickFilter quickFilterParser={(input) => [input]} />
          {refreshFunction && (
            <button
              className="px-2 py-1 rounded font-display font-semibold focus:outline-none hover:bg-slate-200"
              onClick={refreshFunction}
            >
              <Icon icon="refresh" />
            </button>
          )}
        </GridToolbarContainer>
      );
    }

    const getRowHeight = React.useCallback(() => "auto", []);
    const getEstimatedRowHeight = React.useCallback(() => 200, []);

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
        className={enhancedStyling ? "labels-table" : ""}
      >
        <ThemeProvider theme={theme}>
          <DataGrid
            getRowClassName={getRowClassName}
            rows={rows}
            columns={columns}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            density={density}
            onDensityChange={setDensity}
            pageSizeOptions={pageSize}
            disableRowSelectionOnClick
            checkboxSelection={checkboxSelection}
            keepNonExistentRowsSelected
            {...(checkboxSelection && { onRowSelectionModelChange: handleRowSelectionModelChange })}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            slots={{ toolbar: CustomToolbar }}
            {...(dynamicRowHeight && { getRowHeight, getEstimatedRowHeight })}
          />
        </ThemeProvider>
      </Box>
    );
  }
);

TableData.displayName = "TableData";

export default TableData;
